import axios from 'axios';
import type {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import userManager from '@/shared/utils/userManager';
import {leerplatformSettingsStore} from '@/shared/stores/leerplatformSettingsStore';

// Function to initialize axios client
async function createClient() {
    const {apiUrl} = await leerplatformSettingsStore.loadEnvironmentConfig();

    const client = axios.create({
        baseURL: apiUrl,
    });

    client.interceptors.request.use(
        async (config) => {
            const user = await userManager.getUser();

            config.headers.Authorization = `Bearer ${user.access_token}`;
            return config;
        },
        (error) => Promise.reject(error),
    );

    return client;
}

// Hier stond `export default await createClient()`. Die top-level await maakt
// de entry-chunk, waar dit bestand in zit, een async module, en daar gaat de
// module-loader van Safari tot en met 26 (en daarmee elke browser op iOS ≤ 26)
// mee de mist in: een dynamische import van een chunk die de entry statisch
// importeert, kan resolven voordat die chunk geëvalueerd is. Voor de route `/*`
// gaf dat "Cannot access 'a' before initialization" en een lege pagina, bij
// ruim een op de tien paginaladingen (#7245). Safari 27 heeft dat opgelost.
//
// Daarom wordt de client nu pas bij de eerste aanroep gemaakt. De aanroepers
// gebruiken alleen de call-signatuur, `client<T>(config)`, en die blijft gelijk.
//
// Niet terugzetten naar een top-level await, en ook elders in de entry geen
// toevoegen: één is genoeg om de hele entry weer async te maken.
let clientPromise: Promise<AxiosInstance> | undefined;

function getClient(): Promise<AxiosInstance> {
    if (!clientPromise) {
        clientPromise = createClient().catch((error) => {
            // Niet de mislukking onthouden: dan zou elke latere aanroep hem
            // herhalen zonder het opnieuw te proberen.
            clientPromise = undefined;
            throw error;
        });
    }

    return clientPromise;
}

async function client<T = unknown, R = AxiosResponse<T>, D = unknown>(
    config: AxiosRequestConfig<D>,
): Promise<R> {
    const instance = await getClient();

    return instance.request<T, R, D>(config);
}

export default client;
